/* The admin console's read-only view of the database.
 *
 * Three calls — the table index, one table's rows with the schema around
 * them, and a profile of one table for data QA — and the CSV export, which
 * is a plain link rather than a fetch so the browser saves the file. Every
 * route is admin-only and read-only; nothing here writes. */

import { request } from './api'

const BASE = '/api'

// ------------------------------------------------------------------ index
export interface TableRef {
  name: string
  rows: number
  columns: number
  doc: string | null
}
export interface TableGroup {
  key: string
  title: string
  sub: string
  tables: TableRef[]
}
export interface TablesIndex {
  groups: TableGroup[]
  total_rows: number
  read_only: true
}

// ----------------------------------------------------------------- browse
export interface Column {
  name: string
  type: string
  nullable: boolean
  primary_key: boolean
  unique: boolean
  /** "table.column", or null. */
  foreign_key: string | null
}

/** One equality filter, sent as `f=column:value`. */
export interface Filter { column: string; value: string }

export type Dir = 'asc' | 'desc'

export interface BrowseQuery {
  limit?: number
  offset?: number
  sort?: string | null
  dir?: Dir | null
  q?: string | null
  filters?: Filter[]
}

export interface Browse {
  table: string
  doc: string | null
  columns: Column[]
  rows: Record<string, unknown>[]
  total: number
  limit: number
  offset: number
  sort: string | null
  dir: Dir | null
  q: string | null
  filters: Filter[]
  /** This table's foreign keys: `column` points at `table.to`. */
  points_at: { column: string; table: string; to: string }[]
  /** Who holds a foreign key into this table. */
  pointed_at_by: { table: string; column: string }[]
  read_only: boolean
}

// ---------------------------------------------------------------- profile
export interface ProfileColumn {
  name: string
  type: string
  nulls: number
  null_pct: number
  distinct: number
  min: unknown
  max: unknown
  /** Empty strings, for text columns. */
  blank: number
  top: { value: unknown; n: number }[] | null
}
export interface Check {
  label: string
  status: 'ok' | 'warn' | 'info'
  detail: string
}
export interface Profile {
  table: string
  rows: number
  columns: ProfileColumn[]
  checks: Check[]
}

// ------------------------------------------------------------------ query
/** The query string for a browse or an export; `f` repeats per filter. */
export function browseQuery(q: BrowseQuery): string {
  const p = new URLSearchParams()
  if (q.limit != null) p.set('limit', String(q.limit))
  if (q.offset != null) p.set('offset', String(q.offset))
  if (q.sort) p.set('sort', q.sort)
  if (q.sort && q.dir) p.set('dir', q.dir)
  if (q.q) p.set('q', q.q)
  for (const f of q.filters ?? []) p.append('f', `${f.column}:${f.value}`)
  const s = p.toString()
  return s ? `?${s}` : ''
}

/** "col:value" as the URL carries it; the value may itself hold colons. */
export function parseFilter(s: string): Filter | null {
  const i = s.indexOf(':')
  if (i <= 0) return null
  return { column: s.slice(0, i), value: s.slice(i + 1) }
}

export const admin = {
  tables: () => request<TablesIndex>('/admin/tables'),
  browse: (name: string, q: BrowseQuery) =>
    request<Browse>(`/admin/tables/${encodeURIComponent(name)}` + browseQuery(q)),
  profile: (name: string) =>
    request<Profile>(`/admin/tables/${encodeURIComponent(name)}/profile`),
  /** The whole filtered, sorted set — no page bounds. */
  exportUrl: (name: string, q: BrowseQuery) =>
    `${BASE}/admin/tables/${encodeURIComponent(name)}/export.csv`
    + browseQuery({ sort: q.sort, dir: q.dir, q: q.q, filters: q.filters }),
}
